import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { SonicTraits } from "../../domain/index.js";
import type { Band } from "../../infra/api.js";
import { useCompositionStore } from "../../stores/compositionStore.js";
import { Button } from "../../ui/Button.js";
import { SonicTraitsCard } from "../library/SonicTraitsCard.js";
import { SonicTraitsEditor } from "../library/SonicTraitsEditor.js";

interface Props {
  /** The song's band, if any — its `aggregated_traits` feed the "herdar" mode. */
  band: Band | undefined;
}

/**
 * "Traços sonoros desta música" — overrides the band-derived sonic traits for
 * one song, or re-inherits them. The override is a `BandSonicTraitsOverridden`
 * musical event; `AudioState.sonicTraitsOverride` (null = inherit, object =
 * custom) is the source of truth and the event enters the project's undo flow.
 */
export function SongSonicTraitsControl({ band }: Props): JSX.Element {
  const projectId = useCompositionStore((s) => s.projectId);
  const override = useCompositionStore((s) => s.audioState.sonicTraitsOverride);
  const isCustom = override !== null;

  // `editing` = the "Personalizar" pickers are open. `draft` holds the edits
  // until the user commits — derived from the current override when opened.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<SonicTraits>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close the editor when the materialized state changes under us (undo, SSE).
  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [override]);

  function openEditor(): void {
    setDraft(override ?? {});
    setEditing(true);
    setError(null);
  }

  async function emit(
    payload: { mode: "custom"; traits: SonicTraits } | { mode: "inherit" },
  ): Promise<void> {
    if (!projectId) return;
    setBusy(true);
    setError(null);
    try {
      await useCompositionStore.getState().appendMechanicalEvent({
        type: "BandSonicTraitsOverridden",
        projectId,
        payload,
      });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between border-b border-border pb-2">
        <p className="catalog-num">TRAÇOS SONOROS · DESTA FAIXA</p>
        <span
          className={`font-mono text-[10px] uppercase tracking-widest2 ${
            isCustom ? "text-butter" : "text-ink-dim"
          }`}
        >
          {isCustom ? "● personalizado" : "○ herdando da banda"}
        </span>
      </div>

      <div className="border border-border bg-tape p-5 shadow-sleeve">
        {editing ? (
          <div className="flex flex-col gap-4">
            <p className="font-mono text-[10px] leading-relaxed text-ink-dim">
              Estes traços substituem os da banda só nesta faixa. A banda continua intacta.
            </p>
            <SonicTraitsEditor value={draft} onChange={setDraft} />
            <div className="flex items-center justify-between border-t border-border pt-3">
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
                cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() => void emit({ mode: "custom", traits: draft })}
                disabled={busy || !projectId}
              >
                {busy ? "salvando…" : "aplicar nesta faixa"}
              </Button>
            </div>
          </div>
        ) : isCustom ? (
          <div className="flex flex-col gap-4">
            <SonicTraitsCard traits={override} />
            <div className="flex items-center justify-between border-t border-border pt-3">
              <Button
                variant="ghost"
                onClick={() => void emit({ mode: "inherit" })}
                disabled={busy || !projectId}
              >
                {busy ? "…" : "↩ voltar a herdar da banda"}
              </Button>
              <Button variant="secondary" onClick={openEditor} disabled={busy}>
                editar traços
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="font-mono text-[10px] leading-relaxed text-ink-dim">
              Esta faixa usa os traços da banda{" "}
              {band ? (
                <Link
                  to={`/bands/${band.id}`}
                  className="text-butter underline-offset-2 hover:underline"
                >
                  {band.name}
                </Link>
              ) : (
                <span className="text-ink-muted">(sem banda)</span>
              )}
              .
            </p>
            <SonicTraitsCard
              traits={band?.aggregated_traits ?? {}}
              emptyHint={
                band
                  ? "A banda não tem traços declarados — a geração fica neutra."
                  : "Faixa sem banda — a geração fica neutra."
              }
            />
            <div className="flex justify-end border-t border-border pt-3">
              <Button variant="secondary" onClick={openEditor} disabled={busy || !projectId}>
                personalizar para esta faixa
              </Button>
            </div>
          </div>
        )}

        {error && (
          <p
            role="alert"
            className="mt-3 border border-vinyl/40 bg-vinyl/5 p-2 font-mono text-[10px] uppercase tracking-widest2 text-vinyl"
          >
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
